import { Alert, AlertDescription, AlertIcon, AlertTitle, AspectRatio, Box, Button, Divider, FormControl, FormErrorMessage, Heading, HStack, Image, Input, InputGroup, SimpleGrid, Text } from "@chakra-ui/react";
import { FC, ReactElement, useEffect, useState } from "react";
import Number from "components/number-format";
import { Field, Formik } from "formik";
import TimerCountdown from "../../components/timer-countdown";
import moment from "moment";

interface CheckoutVoucherProps {
    voucher: any;
    program: any,
    handleSubmition: any;
};

const formInitialValue = {
    voucherCode: ''
};

const CheckoutVoucher: FC<CheckoutVoucherProps> = ({ voucher, program, handleSubmition }): ReactElement => {
    const [formValue, setFormValue] = useState<any>(formInitialValue);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    useEffect(() => {
        setFormValue({ voucherCode: voucher.appliedVoucherCode });
    }, [voucher]);

    const handleFormSubmit = (values: any) => {
        setIsSubmitting(true); handleSubmition(values).then(() => {
            setIsSubmitting(false);
        });
    };

    return (
        <Box px={{ base: 4, md: 8 }} py={8}>
            <Box mb={4}>
                <Heading fontSize={'2xl'} fontWeight={"extrabold"} lineHeight={"short"}>{program.fullName}</Heading>
                <Text mt={1} fontSize={'sm'}>{program.description}</Text>
            </Box>
            <AspectRatio maxW='600px' ratio={16 / 9} flex={"1 1 auto"}>
                <Box borderRadius={"md"}>
                    <Image src={program.posterUrl} objectFit={"cover"} />
                </Box>
            </AspectRatio>
            <Box mt={2}>
                <Text textDecoration={'line-through'} color={'red.700'} fontWeight={'bold'} fontSize={'sm'}>
                    <Number displayType={'text'} prefix={'Rp '} thousandSeparator={true} value={program.priceOriginal} />
                </Text>
                <Text fontWeight={'bold'}>
                    <Text fontWeight={'bold'}>
                        {
                            program.price === 0 ?
                                'GRATIS'
                                :
                                <Number displayType={'text'} prefix={'Rp '} thousandSeparator={true} value={program.price} />
                        }
                    </Text>
                </Text>
            </Box>
            {
                voucher.changeAble &&

                <Formik initialValues={formValue} onSubmit={handleFormSubmit}>
                    {
                        ({ handleSubmit, errors, touched }: any) => (
                            <form onSubmit={handleSubmit} noValidate={true}>
                                <Box bgColor={'white'} mt={2} p={4} rounded={'md'} mb={4}>
                                    <Heading fontSize={'sm'}>Klaim Voucher (Jika ada):</Heading>
                                    <FormControl mt={2} isInvalid={!!(errors.voucherCode && touched.voucherCode)} isDisabled={isSubmitting}>
                                        <InputGroup>
                                            <Field
                                                as={Input}
                                                variant={'outline'}
                                                fontSize={'sm'}
                                                type='text'
                                                placeholder='Masukan Kode Voucher'
                                                id={'voucherCode'}
                                                name={'voucherCode'}
                                                validate={(value: any) => {
                                                    let error;
                                                    if (value.length <= 0)
                                                        error = "Silahkan isi Kode Voucher";
                                                    return error;
                                                }}
                                            />
                                            <Button type="submit" size={'md'} variant={"solid"} colorScheme={"red"} ml={1} isLoading={isSubmitting}>Klaim</Button>
                                        </InputGroup>
                                        <FormErrorMessage>{errors.voucherCode}</FormErrorMessage>
                                    </FormControl>
                                    {
                                        voucher.isApplied &&
                                        <Box mt={2}>
                                            <Alert status={voucher.isValid ? "success" : "error"}>
                                                <AlertIcon />
                                                <Box>
                                                    <AlertTitle>{voucher.isValid ? "Berhasil Klaim!" : "Gagal Klaim!"}</AlertTitle>
                                                    <AlertDescription fontSize={'sm'}>
                                                        {voucher.message}
                                                    </AlertDescription>
                                                </Box>
                                            </Alert>
                                            {
                                                voucher.isValid && voucher.isCanExpire &&
                                                <Box bgColor={"orange.100"} py={2} px={4} width={"full"}>
                                                    <Box>
                                                        <Text fontSize={'sm'} textAlign={"center"}>Voucher hanya berlaku sampai:</Text>
                                                        <Text fontSize={'sm'} textAlign={"center"} fontWeight={"bold"}><span color={'teal'}>{moment(voucher.expiryDate).format("DD MMMM yyyy HH:mm A")}</span></Text>
                                                    </Box>
                                                    <Box>
                                                        <TimerCountdown expiryTimestamp={moment(voucher.expiryDate)} countdownFontSize={"2xl"} textFontSize={'md'} includeDays={true} />
                                                    </Box>
                                                </Box>
                                            }
                                        </Box>
                                    }
                                </Box>
                            </form>
                        )}
                </Formik>
            }

            <SimpleGrid columns={{ base: 2, md: 2 }} spacing={2} my={1} fontSize={'sm'}>
                <Text>Subtotal</Text>
                <Text align={'right'}>
                    <Number displayType={'text'} prefix={'Rp '} thousandSeparator={true} value={program.price} />
                </Text>
            </SimpleGrid>

            {
                voucher.isValid &&
                <>
                    <Divider __css={{ borderColor: '#a4a4a4' }} />

                    <SimpleGrid columns={{ base: 2, md: 2 }} spacing={2} my={1} fontSize={'sm'}>
                        <Text>Discount</Text>
                        <Text align={'right'} fontWeight={'bold'} color={'red.600'}>
                            {
                                voucher.isValid && voucher.isPercentage &&
                                <>
                                    <Text fontWeight={'normal'}>
                                        (
                                        <Number displayType={'text'} prefix={''} thousandSeparator={true} value={((voucher.value * 100) * -1).toFixed(0)} />
                                        %)
                                    </Text>
                                    <Number displayType={'text'} prefix={'Rp '} thousandSeparator={true} value={((program.price * voucher.value) * -1).toFixed(0)} />
                                </>
                            }
                            {
                                voucher.isValid && !voucher.isPercentage &&
                                <Number displayType={'text'} prefix={'Rp '} thousandSeparator={true} value={(voucher.value * -1).toFixed(0)} />
                            }
                        </Text>
                    </SimpleGrid>
                </>
            }

            <Divider __css={{ borderColor: '#a4a4a4' }} />

            <SimpleGrid columns={{ base: 2, md: 2 }} spacing={2} my={1} fontSize={'sm'}>
                <Text>Total</Text>
                <Text align={'right'} fontWeight={'bold'}>
                    {
                        voucher.isValid &&
                        <Number displayType={'text'} prefix={'Rp '} thousandSeparator={true} value={voucher.totalAfterDiscount} />
                    }
                    {
                        !voucher.isValid &&
                        <Number displayType={'text'} prefix={'Rp '} thousandSeparator={true} value={program.price} />
                    }

                </Text>
            </SimpleGrid>
        </Box>
    );
};

export default CheckoutVoucher;